import { useEffect, useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

import type { KenjossetEditorPageProps, KenjossetPhoto } from "@/components/kenjosset/types"

export const PHOTOS_PATH = "/storefront/photos"
export const REORDER_PATH = "/storefront/photos/reorder"
export const HOME_PHOTO_PATH = "/storefront/home_photo"

const VISIT_OPTIONS = { preserveScroll: true } as const

// Mirrors StorePhoto::ALLOWED_CONTENT_TYPES. `image/*` would let the iPhone
// picker hand over a HEIC the server then refuses.
const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/webp,image/gif,image/avif"

interface PhotoEditorProps {
  // The gallery as the server serialized it, or the single hero photo.
  photos: KenjossetPhoto[]
  // "gallery" edits the /portfolio grid, "hero" replaces the / hero image.
  variant: "gallery" | "hero"
  // The untouched, read-only rendering of the page. Shown to every visitor and
  // to the admin until he presses "Modifier", so the storefront out of edit
  // mode stays byte-identical to what it was before this feature.
  children: React.ReactNode
}

// Discreet in-place editor for the Kenjosset photos. It draws nothing at all
// unless the shared `can_edit_store` prop says the viewer administers this
// store — and even then, the server re-checks every write.
export default function PhotoEditor({ photos, variant, children }: PhotoEditorProps) {
  const { props } = usePage<KenjossetEditorPageProps>()
  const [editing, setEditing] = useState(false)

  if (props.can_edit_store !== true) return <>{children}</>

  return (
    <>
      <div className="w-full bg-contour/40 px-6 md:px-12 py-2 flex items-center justify-end gap-4">
        <ErrorMessages errors={props.errors} />
        <button
          type="button"
          className="btn btn-link text-sm underline"
          onClick={() => setEditing((current) => !current)}
        >
          {editing ? "Terminer" : "Modifier"}
        </button>
      </div>

      {editing ? (
        variant === "hero" ? (
          <HeroEditor photo={photos[0]} />
        ) : (
          <GalleryEditor photos={photos} />
        )
      ) : (
        children
      )}
    </>
  )
}

function ErrorMessages({ errors }: { errors?: Record<string, string | string[]> }) {
  const messages = Object.values(errors ?? {})
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((message): message is string => typeof message === "string" && message.length > 0)

  if (messages.length === 0) return null

  return (
    <p role="alert" className="text-sm text-red-600 mr-auto">
      {messages.join(", ")}
    </p>
  )
}

// A hidden <input type="file"> driven by a visible button, so the control can
// carry the storefront's own button styling.
function FilePicker({
  label,
  className,
  onPick,
}: {
  label: string
  className: string
  onPick: (file: File) => void
}) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <>
      <button type="button" className={className} onClick={() => input.current?.click()}>
        {label}
      </button>
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        aria-label={label}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          // Reset so picking the same file twice still fires a change event.
          event.target.value = ""
          if (file) onPick(file)
        }}
      />
    </>
  )
}

function HeroEditor({ photo }: { photo?: KenjossetPhoto }) {
  const replace = (file: File) =>
    router.patch(HOME_PHOTO_PATH, { store: { home_photo: file } }, VISIT_OPTIONS)

  return (
    <div className="min-h-[calc(100vh-500px)] h-full">
      {photo && (
        <img
          src={photo.url}
          alt=""
          className="mx-auto h-screen px-6 sm:px-12 w-full object-cover object-top"
        />
      )}
      <div className="flex justify-center py-6">
        <FilePicker label="Remplacer la photo" className="btn btn-primary" onPick={replace} />
      </div>
    </div>
  )
}

function GalleryEditor({ photos }: { photos: KenjossetPhoto[] }) {
  // Local copy so the grid follows the finger/mouse straight away; the server
  // is the source of truth once the reorder round-trip lands.
  const [order, setOrder] = useState(photos)

  useEffect(() => setOrder(photos), [photos])

  // TouchSensor alongside PointerSensor so reordering works on a phone; the
  // activation delay keeps a plain tap from starting a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const sortable = order.every((photo) => photo.id !== null)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const from = order.findIndex((photo) => photo.id === active.id)
    const to = order.findIndex((photo) => photo.id === over.id)
    if (from < 0 || to < 0) return

    const moved = arrayMove(order, from, to)
    setOrder(moved)

    router.patch(
      REORDER_PATH,
      { positions: moved.map((photo, index) => ({ id: photo.id as number, position: index + 1 })) },
      VISIT_OPTIONS
    )
  }

  const add = (file: File) => router.post(PHOTOS_PATH, { store_photo: { image: file } }, VISIT_OPTIONS)

  const grid = (
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 pt-12">
      {order.map((photo) =>
        photo.id === null ? (
          <StaticTile key={photo.url} photo={photo} />
        ) : (
          <SortablePhoto key={photo.id} photo={photo} />
        )
      )}
    </div>
  )

  return (
    <div className="min-h-[calc(100vh-500px)] h-full">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-6 flex items-center gap-4">
        <FilePicker label="Ajouter une photo" className="btn btn-primary" onPick={add} />
        {!sortable && (
          <p className="text-sm text-midgray">
            Galerie encore servie depuis les fichiers du site : ajoutez une photo pour la reprendre
            en main.
          </p>
        )}
      </div>

      {sortable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={order.map((photo) => photo.id as number)}
            strategy={rectSortingStrategy}
          >
            {grid}
          </SortableContext>
        </DndContext>
      ) : (
        grid
      )}
    </div>
  )
}

function StaticTile({ photo }: { photo: KenjossetPhoto }) {
  return (
    <img
      src={photo.url}
      alt={photo.alt}
      loading="lazy"
      className="w-full h-64 md:h-48 object-cover object-center"
    />
  )
}

function SortablePhoto({ photo }: { photo: KenjossetPhoto }) {
  const id = photo.id as number
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const replace = (file: File) =>
    router.patch(`${PHOTOS_PATH}/${id}`, { store_photo: { image: file } }, VISIT_OPTIONS)

  const remove = () => router.delete(`${PHOTOS_PATH}/${id}`, VISIT_OPTIONS)

  // Saved on blur rather than on every keystroke: one request per description,
  // and no round-trip fighting the caret while the admin is still typing.
  const saveAlt = (value: string) => {
    if (value === photo.alt) return

    router.patch(`${PHOTOS_PATH}/${id}`, { store_photo: { alt_text: value } }, VISIT_OPTIONS)
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "relative opacity-60" : "relative"}
    >
      {/* touch-action is not inherited, so it belongs on the element that
          carries the listeners: without it the browser scrolls the page
          instead of letting dnd-kit start a touch drag. */}
      <img
        src={photo.url}
        alt={photo.alt}
        loading="lazy"
        className="w-full h-64 md:h-48 object-cover object-center"
        style={{ touchAction: "none" }}
        {...attributes}
        {...listeners}
      />
      <input
        type="text"
        defaultValue={photo.alt}
        maxLength={200}
        placeholder="Décrire la photo"
        aria-label="Description de la photo"
        className="absolute inset-x-0 top-0 w-full bg-light/80 px-1 py-0.5 text-xs"
        onBlur={(event) => saveAlt(event.target.value)}
      />
      <div className="absolute inset-x-0 bottom-0 flex justify-between gap-2 bg-light/80 p-1">
        <FilePicker label="Remplacer" className="text-xs underline" onPick={replace} />
        <button type="button" className="text-xs underline" onClick={remove}>
          Supprimer
        </button>
      </div>
    </div>
  )
}
